import { useEffect, useState } from 'react';
import { ArrowRight, Lock, Mail } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { authApi } from '../../lib/api';

export function LoginPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const { isAuthenticated, login, needsSetup } = useAuth();
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [loading, setLoading] = useState(false);
	const [oidcProviders, setOidcProviders] = useState<Array<{ slug: string; name: string }>>([]);
	const [oidcLoading, setOidcLoading] = useState<string | null>(null);

	useEffect(() => {
		if (needsSetup) {
			navigate('/setup', { replace: true });
		}
		if (isAuthenticated) {
			navigate('/dashboard', { replace: true });
		}
	}, [isAuthenticated, navigate, needsSetup]);

	useEffect(() => {
		let mounted = true;

		void authApi.publicConfig()
			.then((config) => {
				if (mounted) {
					setOidcProviders(config.oauthProviders ?? []);
				}
			})
			.catch(() => {
				if (mounted) {
					setOidcProviders([]);
				}
			});

		return () => {
			mounted = false;
		};
	}, []);

	return (
		<div className="flex min-h-screen items-center justify-center bg-canvas px-4 py-10">
			<div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.18),_transparent_35%),radial-gradient(circle_at_bottom_right,_rgba(34,197,94,0.12),_transparent_30%)]" />
			<div className="relative grid w-full max-w-6xl gap-8 overflow-hidden rounded-[2rem] border border-subtle bg-canvas/80 p-4 shadow-panel backdrop-blur-2xl lg:grid-cols-[1.15fr_0.85fr] lg:p-6">
				<section className="relative overflow-hidden rounded-[1.75rem] border border-subtle bg-gradient-to-br from-accent/20 via-accent to-accent/5 p-8 lg:p-12">
					<p className="text-sm font-semibold uppercase tracking-[0.35em] text-accent">Cloudflare-ready CRM</p>
					<h1 className="mt-6 max-w-xl text-4xl font-semibold tracking-tight text-primary lg:text-6xl">
						Own the full revenue motion from pipeline to inbox.
					</h1>
					<p className="mt-6 max-w-xl text-base leading-7 text-secondary lg:text-lg">
						A modern operational cockpit for sales teams, relationship managers, and admin operators with a fast React experience and an API-first Go backend.
					</p>
					<div className="mt-10 grid gap-4 sm:grid-cols-3">
						{[
							['Unified pipeline', 'Track revenue, tasks, and account health in one place'],
							['Embedded inbox', 'Work email without leaving the CRM'],
							['Admin control', 'Configure auth, domains, and automation centrally']
						].map(([title, copy]) => (
							<div key={title} className="rounded-3xl border border-subtle bg-surface-2 p-4">
								<p className="font-medium text-primary">{title}</p>
								<p className="mt-2 text-sm leading-6 text-muted">{copy}</p>
							</div>
						))}
					</div>
				</section>
				<section className="rounded-[1.75rem] border border-subtle bg-surface-2/90 p-8 lg:p-10">
					<p className="text-sm font-semibold uppercase tracking-[0.3em] text-muted">Welcome back</p>
					<h2 className="mt-4 text-3xl font-semibold text-primary">Sign in to GalloCRM</h2>
					<p className="mt-3 text-sm leading-6 text-muted">Use your configured identity provider for MVP access. Local credentials can remain available as a fallback.</p>
					{oidcProviders.length > 0 ? (
						<div className="mt-8 space-y-3">
							{oidcProviders.map((provider) => (
								<button
									key={provider.slug}
									type="button"
									disabled={oidcLoading === provider.slug}
									onClick={async () => {
										setOidcLoading(provider.slug);
										try {
											const response = await authApi.oidcStart(provider.slug);
											const authUrl = response.authURL ?? response.auth_url;
											if (authUrl) {
												window.location.assign(authUrl);
											}
										} finally {
											setOidcLoading(null);
										}
									}}
									className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-cyan-400/30 bg-accent/10 px-4 py-3 text-sm font-semibold text-cyan-100 transition hover:bg-accent/20 disabled:cursor-not-allowed disabled:opacity-60"
								>
									{oidcLoading === provider.slug ? `Redirecting to ${provider.name}...` : `Continue with ${provider.name}`}
									<ArrowRight className="h-4 w-4" />
								</button>
							))}
						</div>
					) : null}
					<form
						className="mt-10 space-y-5"
						onSubmit={async (event) => {
							event.preventDefault();
							setLoading(true);
							const ok = await login(email, password);
							setLoading(false);
							if (ok) {
								const next = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || '/dashboard';
								navigate(next, { replace: true });
							}
						}}
					>
						{oidcProviders.length > 0 ? <p className="text-xs font-medium uppercase tracking-[0.25em] text-muted">Local fallback</p> : null}
						<label className="block space-y-2">
							<span className="text-sm font-medium text-secondary">Email</span>
							<div className="flex items-center gap-3 rounded-2xl border border-subtle bg-surface-2 px-4 py-3">
								<Mail className="h-4 w-4 text-muted" />
								<input value={email} onChange={(event) => setEmail(event.target.value)} type="email" required placeholder="you@company.com" className="w-full border-none bg-transparent p-0 text-primary placeholder:text-muted focus:outline-none focus:ring-0" />
							</div>
						</label>
						<label className="block space-y-2">
							<span className="text-sm font-medium text-secondary">Password</span>
							<div className="flex items-center gap-3 rounded-2xl border border-subtle bg-surface-2 px-4 py-3">
								<Lock className="h-4 w-4 text-muted" />
								<input value={password} onChange={(event) => setPassword(event.target.value)} type="password" required placeholder="Enter your password" className="w-full border-none bg-transparent p-0 text-primary placeholder:text-muted focus:outline-none focus:ring-0" />
							</div>
						</label>
						<button disabled={loading} type="submit" className="btn-primary w-full transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60">
							{loading ? 'Signing in...' : 'Sign in'}
							<ArrowRight className="h-4 w-4" />
						</button>
					</form>
					<div className="mt-8 rounded-2xl border border-subtle bg-surface-2 p-4 text-sm text-muted">
						Need first-run provisioning? <Link className="font-medium text-accent hover:text-accent" to="/setup">Open setup</Link>
					</div>
				</section>
			</div>
		</div>
	);
}
